




import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Divider,
  Typography,
} from "@mui/material";
import CustomText, { textStyles } from "../../../../components/CustomText";
import profilecross from "../../../../assets/profilecross.svg";
import down from "../../../../assets/down.svg";
import checkin from "../../../../assets/Attendance/checkin.svg";
import checkout from "../../../../assets/Attendance/checkout.svg";
import { useTranslation } from "react-i18next";
import StatusDropdown from "../../../../components/StatusDropdown";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import dummy from "../../../../assets/dummy.png";
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils";
import {
  getGoogleMapsPinIconForStatus,
  getStatusBadgeBackgroundColor,
} from "../../../../constants/statusPalette";

const REVIEW_STATUS_LABEL_MAP = {
  Validated: "unvalidatedPunches.statusLabels.validatedPunch",
  Invalid: "unvalidatedPunches.statusLabels.unvalidatedPunch",
  Pending: "unvalidatedPunches.statusLabels.pendingPunch",
};

const mapReviewStatusToUI = (reviewStatus) => {
  if (!reviewStatus) return null;
  switch (String(reviewStatus).trim().toUpperCase()) {
    case "APPROVED":
      return "Validated";
    case "REJECTED":
      return "Invalid";
    case "PENDING":
      return "Pending";
    default:
      return null;
  }
};

const mapContainerStyle = { width: "100%", height: "100%" };

/** Same local time display as punch times (12h, en-GB) via dateTimeUtils. */
const LOCAL_TIME_DISPLAY = {
  locale: "en-GB",
  formatOptions: {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  },
};

const normalizeStatusKey = (status) =>
  String(status || "")
    .trim()
    .replace(/\s+/g, "_")
    .toUpperCase();

// Reusable Section Header — status dot uses same fill as StatusDropdown badge
const SectionHeader = ({ label, icon, sectionicon, statusKey }) => (
  <Box mt={2.5} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    {statusKey ? (
      <Box
        aria-hidden
        sx={{
          width: 18,
          height: 16,
          borderRadius: "4px",
          flexShrink: 0,
          backgroundColor: getStatusBadgeBackgroundColor(statusKey),
        }}
      />
    ) : (
      sectionicon && (
        <img src={sectionicon} width={18} height={18} alt="section icon" />
      )
    )}
    <CustomText
      sx={{
        ...textStyles.h1,
        fontSize: 18,
        fontWeight: 600,
        color: "#000000",
      }}
    >
      {label}
    </CustomText>
    {/* <img src={icon} width={20} height={20} alt="dropdown icon" /> */}
  </Box>
);

// Reusable Box
const InfoBox = ({ children }) => (
  <Box
    mt={2}
    sx={{
      border: "1px solid #091E4224",
      borderRadius: "10px",
      px: 2,
      py: 2,
    }}
  >
    {children}
  </Box>
);

// Reusable Row
const InfoRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 2,
      mb: 2,
    }}
  >
    <CustomText
      sx={{
        ...textStyles.h1,
        fontSize: 16,
        fontWeight: 400,
        color: "#5E5C5C",
      }}
    >
      {label}
    </CustomText>
    <CustomText
      sx={{
        ...textStyles.h1,
        fontSize: 15,
        fontWeight: 400,
        color: "#172B4D",
        textAlign: "right",
        marginLeft: "auto",
        maxWidth: "65%",
        wordBreak: "break-word",
      }}
    >
      {value || "--"}
    </CustomText>
  </Box>
);

export default function AttendanceDetailsDrawer({ open, onClose, selectedRow }) {
  const { t } = useTranslation();
  const [activePunchIndex, setActivePunchIndex] = useState(null);
  const mapRef = useRef(null);

  const formatUTCToLocal = (utcString) => {
    const localValue = convertUTCToLocalDateTime(utcString, LOCAL_TIME_DISPLAY);
    return localValue === "—" ? "--" : localValue;
  };

  /** API schedule strings (e.g. "09:00:00") — normalized and formatted like punch times. */
  const formatScheduleWallTime = (timeValue) => {
    if (timeValue === null || timeValue === undefined || timeValue === "") return "--";
    const localValue = convertUTCToLocalDateTime(timeValue, LOCAL_TIME_DISPLAY);
    return localValue === "—" ? "--" : localValue;
  };

  const getMarkerIcon = (status) => getGoogleMapsPinIconForStatus(status);

  const resolvedStatus =
    selectedRow?.lastStatus ||
    selectedRow?.workerStatus ||
    selectedRow?.attendanceCategory ||
    "-";

  const isAbsentStatus = normalizeStatusKey(resolvedStatus) === "ABSENT";

  const formatStatusLabel = (status) => {
    if (!status || status === "-") return "-";
    return String(status)
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const renderValidationBadge = (isValid) => {
    if (typeof isValid !== "boolean") return "--";

    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 84,
          px: 1.25,
          py: 0.35,
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: 500,
          fontFamily: "Poppins, sans-serif",
          color: isValid ? "#216E4E" : "#A12622",
          backgroundColor: isValid ? "#DCF3E8" : "#FDE8E8",
          border: isValid ? "1px solid #9BD3BA" : "1px solid #F4B6B6",
        }}
      >
        {isValid ? t("attendanceDrawer.valid") : t("attendanceDrawer.invalid")}
      </Box>
    );
  };

  const resolvedLocationText =
    selectedRow?.locationText ||
    selectedRow?.location?.text ||
    "--";

  const punches = Array.isArray(selectedRow?.punches) ? selectedRow.punches : [];
  const orderedPunches = punches.filter((p) =>
    ["CLOCK_IN", "BREAK_START", "BREAK_END", "CLOCK_OUT"].includes(p?.actionType)
  );

  const sortedOrderedPunches = [...orderedPunches].sort((a, b) => {
    const aTime = new Date(a?.occurredAt || 0).getTime();
    const bTime = new Date(b?.occurredAt || 0).getTime();
    return bTime - aTime;
  });

  const getLatestPunchOccurredAt = (actionType) => {
    const matching = punches.filter((p) => p?.actionType === actionType);
    if (!matching.length) return null;
    return matching.reduce((latest, punch) => {
      const latestMs = new Date(latest?.occurredAt || 0).getTime();
      const punchMs = new Date(punch?.occurredAt || 0).getTime();
      return punchMs >= latestMs ? punch : latest;
    }).occurredAt ?? null;
  };

  const latestClockInAt = getLatestPunchOccurredAt("CLOCK_IN");
  const latestClockOutAt = getLatestPunchOccurredAt("CLOCK_OUT");

  const rowIssuesBlock = selectedRow?.issues ?? null;
  const rowAttendanceIssues = Array.isArray(rowIssuesBlock?.attendance_issues)
    ? rowIssuesBlock.attendance_issues
    : Array.isArray(selectedRow?.attendance_issues)
      ? selectedRow.attendance_issues
      : [];

  const latestPunchId =
    sortedOrderedPunches[0]?.punchId ??
    sortedOrderedPunches[0]?.id ??
    selectedRow?.punchId ??
    null;

  const resolveTotalIssueCount = () => {
    const fromIssues =
      rowIssuesBlock?.total_issue_count ?? rowIssuesBlock?.total_punch_issue_count;
    if (typeof fromIssues === "number") return fromIssues;
    if (rowAttendanceIssues.length) return rowAttendanceIssues.length;
    return null;
  };

  const resolvePunchIssues = (punch) => {
    if (
      typeof punch?.issues?.issue_count === "number" ||
      punch?.issues?.issue_list?.length
    ) {
      return punch.issues;
    }
    const punchId = punch?.punchId ?? punch?.id;
    if (punchId == null) return null;
    const forPunch = rowAttendanceIssues.filter(
      (issue) => String(issue?.punch_id ?? issue?.punchId) === String(punchId)
    );
    if (forPunch.length) {
      return {
        issue_count: forPunch.length,
        issue_list: [...new Set(forPunch.map((i) => i.type).filter(Boolean))],
      };
    }
    if (latestPunchId != null && String(punchId) === String(latestPunchId)) {
      const last = selectedRow?.last_punch_issues;
      if (last?.issue_count != null || last?.issue_list?.length) return last;
    }
    return null;
  };

  const totalIssueCount = resolveTotalIssueCount();

  const validPunches = punches.filter((p) => {
    const lat = p?.location?.latitude;
    const lng = p?.location?.longitude;
    return typeof lat === "number" && typeof lng === "number";
  });

  const fitMapToPunches = (mapInstance) => {
    if (!mapInstance || !window.google?.maps || validPunches.length === 0) return;

    if (validPunches.length === 1) {
      mapInstance.setCenter({
        lat: validPunches[0].location.latitude,
        lng: validPunches[0].location.longitude,
      });
      mapInstance.setZoom(15);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    validPunches.forEach((punch) => {
      bounds.extend({
        lat: punch.location.latitude,
        lng: punch.location.longitude,
      });
    });
    mapInstance.fitBounds(bounds, 40);
  };

  useEffect(() => {
    if (open && mapRef.current && validPunches.length > 0) {
      fitMapToPunches(mapRef.current);
    }
  }, [open, validPunches.length]);

  const scheduleClockInClassifications = new Set([
    "EARLY_CLOCK_IN",
    "ON_TIME_CLOCK_IN",
    "LATE_CLOCK_IN",
  ]);
  const scheduleClockOutClassifications = new Set([
    "EARLY_CLOCK_OUT",
    "ON_TIME_CLOCK_OUT",
    "LATE_CLOCK_OUT",
  ]);

  const schedulePinStatusKeys = new Set([
    ...scheduleClockInClassifications,
    ...scheduleClockOutClassifications,
  ]);

  /**
   * Status key for map pin fill — must match StatusDropdown / `statusPalette` badge background.
   * Prefer schedule classification (e.g. LATE_CLOCK_IN) over raw actionType (CLOCK_IN).
   */
  const getPunchStatusForPin = (punch) => {
    const classification = String(punch?.schedule_timing?.classification || "")
      .trim()
      .toUpperCase();
    if (schedulePinStatusKeys.has(classification)) {
      return classification;
    }

    const actionType = String(punch?.actionType || "")
      .trim()
      .toUpperCase();
    if (actionType === "ON_BREAK") return "BREAK_START";
    if (actionType) return actionType;

    return resolvedStatus;
  };

  const getPunchMeta = (actionType, scheduleClassification) => {
    const cls = String(scheduleClassification || "").trim().toUpperCase();

    if (actionType === "CLOCK_IN" && scheduleClockInClassifications.has(cls)) {
      const label = t(`attendanceDrawer.attendanceIssueValues.${cls}`, {
        defaultValue: t("clockIn"),
      });
      return {
        title: label,
        timeLabel: label,
        locationLabel: t("attendanceDrawer.checkInLocation"),
        icon: checkin,
      };
    }
    if (actionType === "CLOCK_OUT" && scheduleClockOutClassifications.has(cls)) {
      const label = t(`attendanceDrawer.attendanceIssueValues.${cls}`, {
        defaultValue: t("clockOut"),
      });
      return {
        title: label,
        timeLabel: label,
        locationLabel: t("attendanceDrawer.checkOutLocation"),
        icon: checkout,
      };
    }

    switch (actionType) {
      case "CLOCK_IN":
        return {
          title: t("clockIn"),
          timeLabel: t("clockIn"),
          locationLabel: t("attendanceDrawer.checkInLocation"),
          icon: checkin,
        };
      case "BREAK_START":
        return {
          title: t("breakStart"),
          timeLabel: t("breakStart"),
          locationLabel: t("attendanceDrawer.breakStartLocation"),
          icon: checkin,
        };
      case "BREAK_END":
        return {
          title: t("breakEnd"),
          timeLabel: t("breakEnd"),
          locationLabel: t("attendanceDrawer.breakEndLocation"),
          icon: checkout,
        };
      case "CLOCK_OUT":
        return {
          title: t("clockOut"),
          timeLabel: t("clockOut"),
          locationLabel: t("attendanceDrawer.checkOutLocation"),
          icon: checkout,
        };
      default:
        return {
          title: actionType || "--",
          timeLabel: actionType || "--",
          locationLabel: t("attendanceDrawer.location"),
          icon: checkout,
        };
    }
  };

  const formatScheduleClassification = (classification) => {
    const normalized = String(classification || "").trim().toUpperCase();

    if (!normalized || normalized === "NOT_APPLICABLE") {
      return "--";
    }

    const supportedClassifications = new Set([
      "EARLY_CLOCK_IN",
      "ON_TIME_CLOCK_IN",
      "LATE_CLOCK_IN",
      "EARLY_CLOCK_OUT",
      "ON_TIME_CLOCK_OUT",
      "LATE_CLOCK_OUT",
    ]);

    if (!supportedClassifications.has(normalized)) {
      return "--";
    }

    return t(`attendanceDrawer.attendanceIssueValues.${normalized}`, {
      defaultValue: normalized
        .split("_")
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(" "),
    });
  };

  /** Schedule vs punch: on-time window = valid; late or early vs schedule = invalid; no schedule rule = na. */
  const getScheduleMatchKind = (classification) => {
    const normalized = String(classification || "").trim().toUpperCase();
    if (!normalized || normalized === "NOT_APPLICABLE") return "na";
    if (normalized === "ON_TIME_CLOCK_IN" || normalized === "ON_TIME_CLOCK_OUT") return "valid";
    if (
      normalized === "LATE_CLOCK_IN" ||
      normalized === "EARLY_CLOCK_IN" ||
      normalized === "EARLY_CLOCK_OUT" ||
      normalized === "LATE_CLOCK_OUT"
    ) {
      return "invalid";
    }
    return "na";
  };

  const renderScheduleMatchBadge = (kind) => {
    if (kind === "valid") return renderValidationBadge(true);
    if (kind === "invalid") return renderValidationBadge(false);
    return "--";
  };

  const formatMinutesDurationLabel = (minutes) => {
    if (minutes === null || minutes === undefined || Number.isNaN(Number(minutes))) return "--";
    const total = Math.max(0, Math.round(Number(minutes)));
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h > 0 && m > 0) {
      return h === 1
        ? t("attendanceDrawer.durationOneHourAndMinutes", { minutes: m })
        : t("attendanceDrawer.durationHoursMinutes", { hours: h, minutes: m });
    }
    if (h > 0) {
      return h === 1
        ? t("attendanceDrawer.durationHourOnly")
        : t("attendanceDrawer.durationHoursOnly", { hours: h });
    }
    return t("attendanceDrawer.durationMinutesOnly", { count: m });
  };

  const formatMinutesFromSchedule = (minutes) => {
    if (minutes === null || minutes === undefined || Number.isNaN(Number(minutes))) {
      return "--";
    }
    const n = Number(minutes);
    if (n === 0) return t("attendanceDrawer.minutesOnSchedule");
    const durationLabel = formatMinutesDurationLabel(Math.abs(n));
    if (n > 0) return t("attendanceDrawer.minutesLateVsSchedule", { duration: durationLabel });
    return t("attendanceDrawer.minutesEarlyVsSchedule", { duration: durationLabel });
  };

  const formatScheduledWindow = (st) => {
    const start = st?.scheduleStart;
    const end = st?.scheduleEnd;
    const grace = st?.graceMinutes;
    if (!start || !end) return "--";
    const startDisp = formatScheduleWallTime(start);
    const endDisp = formatScheduleWallTime(end);
    if (startDisp === "--" || endDisp === "--") {
      return grace !== null && grace !== undefined
        ? t("attendanceDrawer.scheduledWindowValue", { start, end, grace })
        : `${start} – ${end}`;
    }
    return grace !== null && grace !== undefined
      ? t("attendanceDrawer.scheduledWindowValue", {
          start: startDisp,
          end: endDisp,
          grace,
        })
      : `${startDisp} – ${endDisp}`;
  };

  const formatIssueListText = (issues) => {
    if (!issues?.issue_list?.length) return "--";
    return issues.issue_list
      .map((key) =>
        t(`attendanceDrawer.issueKeys.${key}`, {
          defaultValue: String(key).replace(/_/g, " "),
        })
      )
      .join(", ");
  };

  const resolveWorkingMinutesDisplay = (minutes, hoursDecimal) => {
    if (minutes !== null && minutes !== undefined && !Number.isNaN(Number(minutes))) {
      return formatMinutesDurationLabel(Number(minutes));
    }
    if (hoursDecimal !== null && hoursDecimal !== undefined && !Number.isNaN(Number(hoursDecimal))) {
      return formatMinutesDurationLabel(Math.round(Number(hoursDecimal) * 60));
    }
    return "--";
  };

  const formatScheduledWorkingFromSchedule = (schedule) => {
    const fromScheduled = resolveWorkingMinutesDisplay(
      schedule?.scheduledWorkingMinutes,
      schedule?.scheduledWorkingHours
    );
    if (fromScheduled !== "--") return fromScheduled;
    return formatMinutesDurationLabel(schedule?.workingHoursMinutes);
  };
  const formatCompletedWorkingFromSchedule = (schedule) => {
    const mins = schedule?.completedWorkingMinutes;
    const hrs = schedule?.completedWorkingHours;
    const hasMins = mins !== null && mins !== undefined && !Number.isNaN(Number(mins));
    const hasHrs = hrs !== null && hrs !== undefined && !Number.isNaN(Number(hrs));
    if (!hasMins && !hasHrs) return "--";
    return resolveWorkingMinutesDisplay(
      hasMins ? mins : null,
      hasHrs ? hrs : null
    );
  };

  const formatScheduledBreakFromSchedule = (schedule) => {
    const raw = schedule?.breakDurationMinutes ?? schedule?.breakPolicy?.unpaid ?? null;
    if (raw === null || raw === undefined || Number.isNaN(Number(raw))) return "--";
    return formatMinutesDurationLabel(Number(raw));
  };

  const formatTakenBreakFromSchedule = (schedule) => {
    const raw = schedule?.takenBreakMinutes;
    if (raw === null || raw === undefined || Number.isNaN(Number(raw))) return "--";
    return formatMinutesDurationLabel(Number(raw));
  };

  const buildRowScheduleSummary = () => {
    if (isAbsentStatus) return t("attendanceDrawer.rowScheduleSummaryAbsent");
    const latest = sortedOrderedPunches[0];
    const schedule = selectedRow?.schedule;
    const start = selectedRow?.scheduledStartTime ?? schedule?.startTime;
    const end = selectedRow?.scheduledEndTime ?? schedule?.endTime;

    if (!latest) {
      if (start && end) {
        return t("attendanceDrawer.rowScheduleSummaryNoPunch", {
          start: formatScheduleWallTime(start),
          end: formatScheduleWallTime(end),
        });
      }
      const cat =
        selectedRow?.computed?.attendanceCategory || selectedRow?.attendanceCategory;
      return cat ? formatStatusLabel(cat) : "--";
    }

    const kind = getScheduleMatchKind(latest.schedule_timing?.classification);
    const outcome = formatScheduleClassification(latest.schedule_timing?.classification);
    const mins = formatMinutesFromSchedule(latest.schedule_timing?.minutesFromSchedule);

    const segments = [];
    if (kind === "valid" || kind === "invalid") {
      segments.push(
        kind === "valid" ? t("attendanceDrawer.valid") : t("attendanceDrawer.invalid")
      );
    }
    if (outcome !== "--") segments.push(outcome);
    if (mins !== "--") segments.push(mins);
    return segments.length ? segments.join(" · ") : "--";
  };

  const InfoRowValue = ({ value }) => {
    if (typeof value === "string" || typeof value === "number") {
      return (
        <CustomText
          sx={{
            ...textStyles.h1,
            fontSize: 15,
            fontWeight: 400,
            color: "#172B4D",
            textAlign: "right",
            marginLeft: "auto",
            maxWidth: "65%",
            wordBreak: "break-word",
          }}
        >
          {value || "--"}
        </CustomText>
      );
    }
    return value || "--";
  };

  const DrawerInfoRow = ({ label, value }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 2,
        mb: 2,
      }}
    >
      <CustomText
        sx={{
          ...textStyles.h1,
          fontSize: 16,
          fontWeight: 400,
          color: "#5E5C5C",
          textAlign: "left",
        }}
      >
        {label}
      </CustomText>
      <InfoRowValue value={value} />
    </Box>
  );

  const EvidenceBlock = ({ punch }) => (
    <>
      <DrawerInfoRow
        label={t("attendanceDrawer.evidence")}
        value={
          punch?.evidencePhoto ? (
            <img
              crossOrigin="anonymous"
              src={punch.evidencePhoto}
              alt="Evidence"
              style={{
                width: 60,
                height: 60,
                borderRadius: 8,
                objectFit: "cover",
                border: "1px solid #ddd",
              }}
              onError={(e) => (e.currentTarget.src = dummy)}
            />
          ) : (
            "--"
          )
        }
      />
      <DrawerInfoRow
        label={t("attendanceDrawer.faceMatched")}
        value={renderValidationBadge(punch?.validation?.faceMatched)}
      />
      <DrawerInfoRow
        label={t("attendanceDrawer.locationValidated")}
        value={renderValidationBadge(
          punch?.validation?.locationMatched ?? punch?.validation?.locationValidated
        )}
      />
      <DrawerInfoRow
        label={t("attendanceDrawer.reviewStatus")}
        value={
          mapReviewStatusToUI(punch?.validation?.reviewStatus) ? (
            <Box sx={{ pointerEvents: "none" }}>
              <StatusDropdown
                currentStatus={mapReviewStatusToUI(punch?.validation?.reviewStatus)}
                labelMap={REVIEW_STATUS_LABEL_MAP}
              />
            </Box>
          ) : (
            "--"
          )
        }
      />
    </>
  );

  if (!selectedRow) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 580 }, borderRadius: 0 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#E7EBEE",
          p: 2,
          py: 1,
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {selectedRow?.fullTimestamp?.split(" ")?.[0] || t("dashboard.liveAttendance")}
          </CustomText>
        </Box>
      </Box>
      <Divider />

      {/* Body */}
      <Box sx={{ px: 2, py: 2, overflowY: "auto" }}>
        <>
            {/* Punch Details */}
            <SectionHeader
              label={t("attendanceDrawer.punchDetails")}
              icon={down}
              statusKey={selectedRow?.lastStatus || resolvedStatus}
            />
            <InfoBox>
              <DrawerInfoRow
                label={t("attendanceDrawer.workerName")}
                value={selectedRow?.fullName || selectedRow?.workerName || "--"}
              />
              <DrawerInfoRow
                label={t("common.department")}
                value={selectedRow?.department || "--"}
              />
              <DrawerInfoRow
                label={t("common.email")}
                value={selectedRow?.email || "--"}
              />
              <DrawerInfoRow
                label={t("attendanceDrawer.status")}
                value={
                  <Box sx={{ pointerEvents: "none" }}>
                    <StatusDropdown currentStatus={selectedRow?.lastStatus || resolvedStatus} />
                  </Box>
                }
              />
              <DrawerInfoRow
                label={t("clockIn")}
                value={formatUTCToLocal(latestClockInAt)}
              />
              <DrawerInfoRow
                label={t("clockOut")}
                value={formatUTCToLocal(latestClockOutAt)}
              />
              <DrawerInfoRow
                label={t("attendanceDrawer.location")}
                value={isAbsentStatus ? "--" : resolvedLocationText}
              />
              <DrawerInfoRow
                label={t("attendanceDrawer.scheduledWorkingHoursLabel")}
                value={formatScheduledWorkingFromSchedule(selectedRow?.schedule)}
              />
              <DrawerInfoRow
                label={t("attendanceDrawer.completedWorkingHoursLabel")}
                value={formatCompletedWorkingFromSchedule(selectedRow?.schedule)}
              />
              <DrawerInfoRow
                label={t("attendanceDrawer.scheduledBreakAllowance")}
                value={formatScheduledBreakFromSchedule(selectedRow?.schedule)}
              />
              <DrawerInfoRow
                label={t("attendanceDrawer.breakTimeTaken")}
                value={formatTakenBreakFromSchedule(selectedRow?.schedule)}
              />
              <DrawerInfoRow
                label={t("issuesDetails")}
                value={buildRowScheduleSummary()}
              />
              <DrawerInfoRow
                label={t("attendanceDrawer.totalIssuesCount")}
                value={
                  totalIssueCount != null ? String(totalIssueCount) : "--"
                }
              />
            </InfoBox>

            {/* Ordered Punch Timeline */}
            {sortedOrderedPunches.length > 0 ? (
              sortedOrderedPunches.map((punch, index) => {
                const punchIssues = resolvePunchIssues(punch);
                const meta = getPunchMeta(
                  punch?.actionType,
                  punch?.schedule_timing?.classification
                );
                return (
                  <React.Fragment key={`punch-${punch?.punchId || index}`}>
                    <SectionHeader
                      label={meta.title}
                      statusKey={getPunchStatusForPin(punch)}
                    />
                    <InfoBox>
                      <DrawerInfoRow
                        label={meta.timeLabel}
                        value={formatUTCToLocal(punch?.occurredAt)}
                      />
                      <DrawerInfoRow
                        label={meta.locationLabel}
                        value={punch?.location?.locationText || "--"}
                      />
                      <DrawerInfoRow
                        label={t("attendanceDrawer.scheduleMatch")}
                        value={renderScheduleMatchBadge(
                          getScheduleMatchKind(punch?.schedule_timing?.classification)
                        )}
                      />
                      <DrawerInfoRow
                        label={t("attendanceDrawer.attendanceIssue", {
                          defaultValue: "Attendance Issue",
                        })}
                        value={formatScheduleClassification(
                          punch?.schedule_timing?.classification
                        )}
                      />
                      <DrawerInfoRow
                        label={t("attendanceDrawer.minutesFromSchedule")}
                        value={formatMinutesFromSchedule(
                          punch?.schedule_timing?.minutesFromSchedule
                        )}
                      />
                      <DrawerInfoRow
                        label={t("attendanceDrawer.scheduledWindow")}
                        value={formatScheduledWindow(punch?.schedule_timing)}
                      />
                      {typeof punchIssues?.issue_count === "number" &&
                      punchIssues.issue_count > 0 ? (
                        <>
                          <DrawerInfoRow
                            label={t("attendanceDrawer.punchIssueCount")}
                            value={String(punchIssues.issue_count)}
                          />
                          <DrawerInfoRow
                            label={t("issuesDetails")}
                            value={formatIssueListText(punchIssues)}
                          />
                        </>
                      ) : null}
                      <EvidenceBlock punch={punch} />
                    </InfoBox>
                  </React.Fragment>
                );
              })
            ) : !isAbsentStatus ? (
              <>
                <SectionHeader label={t("clockIn")} statusKey="CLOCK_IN" />
                <InfoBox>
                  <DrawerInfoRow label={t("clockIn")} value="--" />
                  <DrawerInfoRow
                    label={t("attendanceDrawer.location")}
                    value={resolvedLocationText}
                  />
                </InfoBox>
              </>
            ) : null}

            {!isAbsentStatus && (
              <>
                <SectionHeader label={t("attendanceDrawer.mapView")} icon={down} />
                <InfoBox>
                  <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden", height: 250 }}>
                    {validPunches.length === 0 ? (
                      <CustomText sx={{ fontSize: "15px" }}>{t("noLocationData")}</CustomText>
                    ) : (
                      <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={{
                          lat: validPunches[0]?.location?.latitude || 37.0902,
                          lng: validPunches[0]?.location?.longitude || -95.7129,
                        }}
                        zoom={13}
                        onLoad={(map) => {
                          mapRef.current = map;
                          fitMapToPunches(map);
                        }}
                        onUnmount={() => {
                          mapRef.current = null;
                        }}
                        options={{
                          fullscreenControl: false,
                          mapTypeControl: false,
                          streetViewControl: false,
                        }}
                      >
                        {validPunches.map((punch, i) => (
                          <Marker
                            key={punch?.punchId || i}
                            position={{
                              lat: punch.location.latitude,
                              lng: punch.location.longitude,
                            }}
                            icon={getMarkerIcon(getPunchStatusForPin(punch))}
                            onClick={() => setActivePunchIndex(i)}
                          />
                        ))}
                        {activePunchIndex !== null && validPunches[activePunchIndex] && (
                          <InfoWindow
                            position={{
                              lat: validPunches[activePunchIndex].location.latitude,
                              lng: validPunches[activePunchIndex].location.longitude,
                            }}
                            onCloseClick={() => setActivePunchIndex(null)}
                          >
                            <Box sx={{ fontFamily: "Poppins, sans-serif" }}>
                              <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
                                {formatStatusLabel(
                                  getPunchStatusForPin(validPunches[activePunchIndex])
                                )}
                              </Typography>
                              <Box sx={{ fontSize: "12px", color: "#44546F" }}>
                                {formatUTCToLocal(validPunches[activePunchIndex]?.occurredAt)}
                              </Box>
                              <Typography sx={{ fontSize: "13px" }}>
                                {validPunches[activePunchIndex]?.location?.locationText}
                              </Typography>
                            </Box>
                          </InfoWindow>
                        )}
                      </GoogleMap>
                    )}
                  </Box>
                </InfoBox>
              </>
            )}
        </>
      </Box>
    </Drawer>
  );
}

